import { useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';

interface EditorToastProps {
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  isVisible: boolean;
  onDismiss: () => void;
  duration?: number;
}

export default function EditorToast({
  type,
  message,
  isVisible,
  onDismiss,
  duration = 3000
}: EditorToastProps) {
  // Auto-dismiss after duration
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(onDismiss, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onDismiss]);

  if (!isVisible) return null;

  const styles = {
    success: {
      bg: 'bg-green-500',
      icon: CheckCircle,
      iconColor: 'text-white'
    },
    error: {
      bg: 'bg-red-500',
      icon: AlertCircle,
      iconColor: 'text-white'
    },
    info: {
      bg: 'bg-blue-500',
      icon: Info,
      iconColor: 'text-white'
    },
    warning: {
      bg: 'bg-yellow-500',
      icon: AlertTriangle,
      iconColor: 'text-white'
    }
  };

  const { bg, icon: Icon, iconColor } = styles[type];

  return (
    <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out max-w-2xl">
      <div className={`${bg} text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3`}>
        <Icon className={`w-5 h-5 flex-shrink-0 ${iconColor}`} />
        <span className="font-medium">{message}</span>
        <button
          onClick={onDismiss}
          className="ml-2 hover:opacity-75 transition-opacity"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

